import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import { analisar } from './validacao.js';

/**
 * Configuração da integração com o ERP do tenant (RF-089).
 *
 * A regra que atravessa este arquivo: **a senha do ERP não existe aqui**. Ela é
 * enviada uma vez, no `PUT /integracao-erp/credencial`, e daí em diante o
 * middleware devolve apenas o ESTADO dela — se existe, de qual usuário e desde
 * quando (RNF-073). Não há tipo, campo nem schema para recebê-la de volta,
 * porque um caminho que não existe não pode ser usado por engano.
 */

const CAMINHO = montarCaminho('/integracao-erp');
const CAMINHO_CREDENCIAL = montarCaminho('/integracao-erp/credencial');

/** O único ERP suportado hoje. O campo existe para o segundo não virar um `if`. */
export const SISTEMAS = [{ valor: 'SeniorErp5', rotulo: 'Senior Gestão Empresarial 5.10.4' }];

export type SistemaErp = 'SeniorErp5';

export const estadoDaCredencialSchema = z.object({
  definida: z.boolean(),
  /** O usuário do ERP, lido do cofre. Nunca a senha. */
  usuario: z.string().nullable(),
  atualizadaEm: z.string().nullable(),
});

export type EstadoDaCredencial = z.infer<typeof estadoDaCredencialSchema>;

export const configuracaoErpSchema = z.object({
  id: z.string(),
  sistema: z.literal('SeniorErp5'),
  endpointBase: z.string(),
  /** `encryption` do web service: 0 (sem criptografia) ou 2. */
  modoAutenticacao: z.number().int(),
  codEmp: z.number().int(),
  codFiliais: z.array(z.number().int()),
  identificadorSistema: z.string(),
  timeoutMs: z.number().int(),
  tamanhoLote: z.number().int(),
  usarMock: z.boolean(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  alteradoEm: z.string(),
});

export type ConfiguracaoErp = z.infer<typeof configuracaoErpSchema>;

const configuracaoComCredencialSchema = configuracaoErpSchema.extend({
  credencial: estadoDaCredencialSchema,
});

export type ConfiguracaoComCredencial = z.infer<typeof configuracaoComCredencialSchema>;

const resultadoDoTesteSchema = z.object({
  alcancavel: z.boolean(),
  statusHttp: z.number().int().nullable(),
  detalhe: z.string(),
  credencialDefinida: z.boolean(),
  verificadoEm: z.string(),
});

export type ResultadoDoTeste = z.infer<typeof resultadoDoTesteSchema>;

export type DadosDaConfiguracao = {
  readonly sistema: SistemaErp;
  readonly endpointBase: string;
  readonly modoAutenticacao: number;
  readonly codEmp: number;
  readonly codFiliais: readonly number[];
  readonly identificadorSistema: string;
  readonly timeoutMs: number;
  readonly tamanhoLote: number;
  readonly usarMock: boolean;
};

export type CredencialDoErp = {
  readonly usuario: string;
  readonly senha: string;
};

/**
 * A configuração do tenant.
 *
 * Responde **404 `INTEGRACAO_NAO_CONFIGURADA`** enquanto ninguém configurou — é
 * o estado inicial de todo cliente novo, e a tela abre o formulário vazio nele
 * em vez de mostrar falha.
 */
export async function obterIntegracao(): Promise<ConfiguracaoComCredencial> {
  return analisar(configuracaoComCredencialSchema, await requisitar(CAMINHO), CAMINHO);
}

export async function salvarIntegracao(dados: DadosDaConfiguracao): Promise<ConfiguracaoErp> {
  const corpo = await requisitar(CAMINHO, { metodo: 'PUT', corpo: dados });
  return analisar(configuracaoErpSchema, corpo, CAMINHO);
}

/**
 * Grava usuário e senha no cofre gerenciado.
 *
 * A resposta é o ESTADO, nunca a credencial: é a última vez que a senha
 * atravessa a rede nesta direção, e ela não volta em resposta nenhuma.
 */
export async function definirCredencialDoErp(
  credencial: CredencialDoErp,
): Promise<EstadoDaCredencial> {
  const corpo = await requisitar(CAMINHO_CREDENCIAL, { metodo: 'PUT', corpo: credencial });
  return analisar(estadoDaCredencialSchema, corpo, CAMINHO_CREDENCIAL);
}

/** Apaga a credencial do cofre — e, junto, desliga a integração. */
export async function removerCredencialDoErp(): Promise<void> {
  await requisitar(CAMINHO_CREDENCIAL, { metodo: 'DELETE' });
}

/**
 * Liga e desliga a integração.
 *
 * Ligar exige credencial no cofre (`CREDENCIAL_ERP_AUSENTE`); desligar não exige
 * nada — é o freio de emergência de quem viu a integração mandando coisa errada
 * para o ERP do cliente.
 */
export async function definirSituacaoDaIntegracao(ativo: boolean): Promise<ConfiguracaoErp> {
  const caminho = montarCaminho('/integracao-erp/situacao');
  const corpo = await requisitar(caminho, { metodo: 'PATCH', corpo: { ativo } });
  return analisar(configuracaoErpSchema, corpo, caminho);
}

/**
 * Confere se o middleware Senior responde.
 *
 * Não prova que a credencial funciona: a requisição vai **sem credencial
 * nenhuma**, e serve para separar endereço errado de serviço fora do ar. Em modo
 * mock, nada sai pela rede — e o `detalhe` diz isso.
 */
export async function testarConexaoComErp(): Promise<ResultadoDoTeste> {
  const caminho = montarCaminho('/integracao-erp/testar-conexao');
  const corpo = await requisitar(caminho, { metodo: 'POST' });
  return analisar(resultadoDoTesteSchema, corpo, caminho);
}
